import React, { useState } from 'react';
import {
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  MoonIcon,
  ShieldCheckIcon,
  SunIcon,
} from '@heroicons/react/24/outline';
import { useLocation } from 'react-router-dom';
import { navigationConfig, type NavigationItem } from '@/config/navigation';
import type { AdminUser } from '@/services/$AuthService';
import { getAdminRole } from '@/utils/adminAccess';

const Bars = Bars3Icon as React.ComponentType<{ className?: string }>;
const Logout = ArrowRightOnRectangleIcon as React.ComponentType<{
  className?: string;
}>;
const Moon = MoonIcon as React.ComponentType<{ className?: string }>;
const Shield = ShieldCheckIcon as React.ComponentType<{ className?: string }>;
const Sun = SunIcon as React.ComponentType<{ className?: string }>;

interface TopBarProps {
  onOpenMobileMenu: () => void;
  onRequestLogout: () => void;
  userAuth: {
    user: AdminUser | null;
  };
}

const ROLE_LABELS: Record<string, string> = {
  manager: 'مدیر',
  superadmin: 'مدیر ارشد',
  admin: 'ادمین',
};

const flattenNavigation = (items: NavigationItem[]): NavigationItem[] =>
  items.flatMap(item => [item, ...flattenNavigation(item.children ?? [])]);

const allNavigationItems = flattenNavigation(navigationConfig);

const findActiveItem = (pathname: string) =>
  allNavigationItems.reduce<NavigationItem | null>((selected, item) => {
    const matches = pathname === item.path || pathname.startsWith(`${item.path}/`);
    if (!matches) return selected;
    if (!selected || item.path.length > selected.path.length) return item;
    return selected;
  }, null);

export const TopBar: React.FC<TopBarProps> = ({ onOpenMobileMenu, onRequestLogout, userAuth }) => {
  const location = useLocation();
  const [isDarkMode, setIsDarkMode] = useState(() =>
    document.documentElement.classList.contains('dark')
  );
  const activeItem = findActiveItem(location.pathname);
  const userName = userAuth.user?.name || userAuth.user?.username || 'مدیر سامانه';
  const roleLabel = ROLE_LABELS[getAdminRole(userAuth.user)] ?? 'کاربر پنل';

  const toggleDarkMode = () => {
    const nextIsDark = !isDarkMode;
    setIsDarkMode(nextIsDark);
    document.documentElement.classList.toggle('dark', nextIsDark);
    localStorage.setItem('theme', nextIsDark ? 'dark' : 'light');
  };

  return (
    <header className="sticky top-0 z-20 border-b border-slate-200/75 bg-[color:var(--pm-bg)]/90 backdrop-blur-xl dark:border-slate-700/75">
      <div className="mx-auto flex h-16 max-w-[1480px] items-center justify-between gap-3 px-4 sm:px-6 lg:px-8">
        <div className="flex min-w-0 items-center gap-3">
          <button
            type="button"
            onClick={onOpenMobileMenu}
            className="pm-icon-button border-slate-200 bg-white lg:hidden dark:border-slate-700 dark:bg-slate-800"
            aria-label="باز کردن منو"
          >
            <Bars className="h-5 w-5" />
          </button>
          <div className="min-w-0">
            <div className="truncate text-sm font-black text-slate-950 dark:text-white">
              {activeItem?.labelPersian ?? 'داشبورد مدیریت'}
            </div>
            <div className="mt-0.5 hidden truncate text-xs font-medium text-slate-500 dark:text-slate-400 sm:block">
              مدیریت یکپارچه خدمات بامبو
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <button
            type="button"
            onClick={toggleDarkMode}
            className="pm-icon-button border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800"
            title={isDarkMode ? 'حالت روشن' : 'حالت تیره'}
            aria-label={isDarkMode ? 'فعال‌سازی حالت روشن' : 'فعال‌سازی حالت تیره'}
          >
            {isDarkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>

          <div className="hidden items-center gap-3 rounded-2xl border border-slate-200 bg-white px-3 py-2 shadow-sm md:flex dark:border-slate-700 dark:bg-slate-800">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
              <Shield className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <div className="max-w-36 truncate text-xs font-black text-slate-900 dark:text-white">
                {userName}
              </div>
              <div className="mt-0.5 truncate text-[11px] font-bold text-emerald-700 dark:text-emerald-300">
                {roleLabel}
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={onRequestLogout}
            className="pm-button-secondary h-10 px-3 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
            aria-label="خروج از حساب کاربری"
          >
            <Logout className="h-5 w-5" />
            <span className="hidden sm:inline">خروج</span>
          </button>
        </div>
      </div>
    </header>
  );
};
